import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, ArrowRight, ListTree, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STANDARD_STAGES = ["LEAD", "QUALIFIED", "PROPOSAL", "NEGOTIATION", "WON", "LOST"];

export default function MappingPage() {
  const [data, setData] = useState(null); // {pipeline, stages, standard_stages}
  const [status, setStatus] = useState(null);
  const [values, setValues] = useState({}); // stage_id -> standard_stage
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const [{ data: st }, { data: mp }] = await Promise.all([
        api.get("/hubspot/status"),
        api.get("/stages/mapping"),
      ]);
      setStatus(st);
      setData(mp);
      const init = {};
      (mp.stages || []).forEach((s) => (init[s.stage_id] = s.current));
      setValues(init);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setVal = (id, val) => {
    setValues((prev) => ({ ...prev, [id]: val }));
    setSaved(false);
  };

  const save = async () => {
    if (!data?.pipeline) return;
    setSaving(true);
    setErr("");
    try {
      await api.post("/stages/mapping", {
        pipeline_id: data.pipeline.pipeline_id,
        mapping: values,
      });
      setSaved(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Not connected
  if (status && !status.connected) {
    return (
      <EmptyState
        title="Connect HubSpot first"
        subtitle="Stage mapping becomes available once you sync at least one pipeline from HubSpot."
        cta="Go to Connect"
        onClick={() => nav("/dashboard/connect")}
        testid="mapping-empty-no-connection"
      />
    );
  }

  // Connected but no pipelines yet (initial sync still running)
  if (data && !data.pipeline) {
    return (
      <EmptyState
        title="Sync in progress"
        subtitle="Pulling your pipelines from HubSpot now. This usually takes under a minute for small portals. Refresh the page in a moment."
        cta="Refresh"
        onClick={load}
        testid="mapping-empty-syncing"
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="mapping-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Step 2</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Map your pipeline stages
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            Match each HubSpot stage in <span className="font-medium text-slate-700">{data?.pipeline?.label}</span> to
            one of our standard stages. We&apos;ve pre-selected best guesses — review, adjust, and confirm.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="mapping-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="mapping-list">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          <div className="col-span-6">HubSpot stage</div>
          <div className="col-span-2 text-right">Probability</div>
          <div className="col-span-4">Map to</div>
        </div>
        {(data?.stages || []).map((s) => (
          <div key={s.stage_id} className="grid grid-cols-12 px-6 py-4 border-b border-slate-100 items-center hover:bg-slate-50/60 transition-colors" data-testid={`mapping-row-${s.stage_id}`}>
            <div className="col-span-6">
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                <ListTree className="h-4 w-4 text-slate-400" />
                {s.label || s.stage_id}
                {s.is_closed && (
                  <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5 rounded">
                    Closed
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">Suggested: {s.suggested}</div>
            </div>
            <div className="col-span-2 text-right text-sm text-slate-600 number-tabular">
              {s.probability != null ? `${Math.round(Number(s.probability) * 100)}%` : "—"}
            </div>
            <div className="col-span-4 flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-slate-400" />
              <select
                data-testid={`mapping-select-${s.stage_id}`}
                value={values[s.stage_id] || s.suggested}
                onChange={(e) => setVal(s.stage_id, e.target.value)}
                className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm bg-white"
              >
                {STANDARD_STAGES.map((s2) => (
                  <option key={s2} value={s2}>{s2}</option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      {err && <div className="text-sm text-red-600" data-testid="mapping-error">{err}</div>}
      {saved && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 text-sm text-emerald-800" data-testid="mapping-saved">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Mapping saved.
          <button
            onClick={() => nav("/dashboard/health")}
            data-testid="mapping-to-health-btn"
            className="ml-auto inline-flex items-center gap-1 text-emerald-800 font-semibold hover:underline"
          >
            See Data Health <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving || !data?.stages?.length}
          data-testid="mapping-confirm-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Confirm mapping"}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle, cta, onClick, testid }) {
  return (
    <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid={testid}>
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        <p className="mt-2 text-sm text-slate-500 max-w-sm">{subtitle}</p>
        <button
          onClick={onClick}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
        >
          {cta}
        </button>
      </div>
    </div>
  );
}
